import React from 'react';
import { AlertCircle, Target, ChevronDown } from 'lucide-react';
import GapCard from '../components/GapCard';

/**
 * PillarGapsTab - Gap analysis with remediation priority
 */
const PillarGapsTab = ({
  pillarCode,
  pillar,
  functions,
  assessments,
  targetLevels,
  onUpdateTargetLevel
}) => {
  const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

  // Calculate gaps
  const gaps = [];
  for (const fn of functions) {
    const currentLevel = assessments[fn.code] || 'TRADITIONAL';
    const targetLevel = targetLevels[fn.code];

    if (targetLevel) {
      const currentIndex = LEVELS.indexOf(currentLevel);
      const targetIndex = LEVELS.indexOf(targetLevel);
      const gap = targetIndex - currentIndex;

      if (gap > 0) {
        gaps.push({
          functionCode: fn.code,
          functionName: fn.name,
          currentLevel,
          targetLevel,
          gap
        });
      }
    }
  }

  // Sort gaps by size (critical first)
  gaps.sort((a, b) => b.gap - a.gap);

  // Functions without targets set
  const functionsWithoutTargets = functions.filter(fn => !targetLevels[fn.code]);

  const formatLevel = (level) => level.charAt(0) + level.slice(1).toLowerCase();

  return (
    <div className="cisa-pillar-gaps">
      {/* Summary */}
      <div className="cisa-pillar-gaps-summary">
        <AlertCircle size={20} style={{ color: pillar.color }} />
        <div>
          <h3>Gap Analysis</h3>
          <p>
            {gaps.length} active gap{gaps.length !== 1 ? 's' : ''} identified for {pillar.name}
          </p>
        </div>
      </div>

      {/* Gap cards */}
      {gaps.length > 0 ? (
        <div className="cisa-pillar-gaps-list">
          {gaps.map(gap => (
            <GapCard
              key={gap.functionCode}
              functionCode={gap.functionCode}
              functionName={gap.functionName}
              pillarName={pillar.name}
              pillarColor={pillar.color}
              currentLevel={gap.currentLevel}
              targetLevel={gap.targetLevel}
            />
          ))}
        </div>
      ) : (
        <div className="cisa-pillar-gaps-empty">
          <Target size={32} />
          <h4>No Active Gaps</h4>
          <p>
            {functionsWithoutTargets.length > 0
              ? `Set target levels for ${functionsWithoutTargets.length} functions to identify gaps.`
              : 'All functions have reached their target maturity levels!'
            }
          </p>
        </div>
      )}

      {/* Set targets for functions without them */}
      {functionsWithoutTargets.length > 0 && (
        <div className="cisa-pillar-gaps-targets">
          <h4>
            <Target size={16} />
            Set Target Levels
          </h4>
          <p className="cisa-pillar-gaps-targets-desc">
            Define where you want to be for these functions:
          </p>

          <div className="cisa-pillar-gaps-targets-list">
            {functionsWithoutTargets.map(fn => {
              const currentLevel = assessments[fn.code] || 'TRADITIONAL';
              const currentIndex = LEVELS.indexOf(currentLevel);
              const availableTargets = LEVELS.slice(currentIndex + 1);

              return (
                <div key={fn.code} className="cisa-pillar-gaps-target-item">
                  <div className="cisa-pillar-gaps-target-info">
                    <span className="cisa-pillar-gaps-target-name">{fn.name}</span>
                    <span className={`cisa-level-badge cisa-level--${currentLevel.toLowerCase()}`}>
                      Currently: {formatLevel(currentLevel)}
                    </span>
                  </div>

                  {availableTargets.length > 0 ? (
                    <div className="cisa-pillar-gaps-target-select-wrap">
                      <select
                        className="cisa-pillar-gaps-target-select"
                        onChange={(e) => onUpdateTargetLevel?.(fn.code, e.target.value)}
                        defaultValue=""
                      >
                        <option value="" disabled>Set target...</option>
                        {availableTargets.map(level => (
                          <option key={level} value={level}>{formatLevel(level)}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="cisa-pillar-gaps-target-chevron" />
                    </div>
                  ) : (
                    <span className="cisa-pillar-gaps-optimal-badge">Already Optimal</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PillarGapsTab;
